import { useMemo } from 'react';

import {
  BoardFertilization,
  BoardSoil,
  BoardStatus,
} from 'features/Croptimizer/types';
import { unMaskInput } from 'utils/helpers';

interface Props {
  boardStatusValues: BoardStatus | null;
  fertilizationValues: BoardFertilization | null;
  getY1CurrentProfit?: string | number;
  getY2CurrentProfit?: string | number;
  getY3CurrentProfit?: string | number;
  getY4CurrentProfit?: string | number;
  getY5CurrentProfit?: string | number;
  getY1CurrentLoss: string;
  getY2CurrentLoss: string;
  getY3CurrentLoss: string;
  getY4CurrentLoss: string;
  getY5CurrentLoss: string;
  boardSoilValues: BoardSoil | null;
}

const useNpkProfit = ({
  fertilizationValues,
  boardStatusValues,
  getY1CurrentLoss,
  getY2CurrentLoss,
  getY3CurrentLoss,
  getY4CurrentLoss,
  getY5CurrentLoss,
  getY1CurrentProfit,
  getY2CurrentProfit,
  getY3CurrentProfit,
  getY4CurrentProfit,
  getY5CurrentProfit,
  boardSoilValues,
}: Props) => {
  const getY1NpkProfit = useMemo(() => {
    if (fertilizationValues && getY1CurrentLoss && boardStatusValues) {
      const lossesPriceN = Number(unMaskInput(fertilizationValues?.nprice));
      const lossesKgN = Number(unMaskInput(fertilizationValues?.nvalue));
      const d9 = () => {
        if (Number(getY1CurrentLoss) > 6.5) {
          return 0;
        } else if (Number(getY1CurrentProfit) < 6.501) {
          const left = 1 - (30 * Number(getY1CurrentLoss) - 95) / 100;
          const right = 1 - (30 * Number(getY1CurrentProfit) - 95) / 100;

          return left - right;
        } else {
          const left = 1 - (30 * Number(getY1CurrentLoss) - 95) / 100;
          const right = 1 - (30 * 6.5 - 95) / 100;

          return left - right;
        }
      };
      const d10 = lossesKgN * lossesPriceN * Number(d9().toFixed(3));

      const lossesPriceK = Number(unMaskInput(fertilizationValues?.kprice));
      const lossesKgK = Number(unMaskInput(fertilizationValues?.kvalue));
      const d17 = () => {
        if (Number(getY1CurrentLoss) > 6) {
          return 0;
        } else if (Number(getY1CurrentProfit) < 6.001) {
          const left = 1 - (40 * Number(getY1CurrentLoss) - 140) / 100;
          const right = 1 - (40 * Number(getY1CurrentProfit) - 140) / 100;

          return left - right;
        } else {
          const left = 1 - (40 * Number(getY1CurrentLoss) - 140) / 100;
          const right = 1 - (40 * 6 - 140) / 100;

          return left - right;
        }
      };
      const d18 = lossesKgK * lossesPriceK * Number(d17().toFixed(3));

      const lossesPriceP = Number(unMaskInput(fertilizationValues?.pprice));
      const lossesKgP = Number(unMaskInput(fertilizationValues?.pvalue));
      const d13 = () => {
        if (Number(getY1CurrentLoss) > 6.5) {
          return 0;
        } else if (Number(getY1CurrentProfit) < 6.501) {
          const left = 1 - (35 * Number(getY1CurrentLoss) - 127.5) / 100;
          const right = 1 - (35 * Number(getY1CurrentProfit) - 127.5) / 100;

          return left - right;
        } else {
          const left = 1 - (35 * Number(getY1CurrentLoss) - 127.5) / 100;
          const right = 1 - (35 * 6.5 - 127.5) / 100;

          return left - right;
        }
      };
      const d14 = lossesKgP * lossesPriceP * Number(d13().toFixed(3));

      if (
        Number(boardStatusValues?.currentPh) <
        Number(boardStatusValues?.targetPh)
      ) {
        return Math.round(d10 + d18 + d14);
      } else {
        const getPercent = () => {
          if (Number(boardSoilValues?.split) === 1) {
            return 1;
          }

          if (Number(boardSoilValues?.split) === 2) {
            return 1 - 0.1;
          }

          if (Number(boardSoilValues?.split) === 3) {
            return 1 - 0.2;
          }

          if (Number(boardSoilValues?.split) === 4) {
            return 1 - 0.3;
          }

          if (Number(boardSoilValues?.split) === 5) {
            return 1 - 0.35;
          }

          return 0;
        };

        return Math.round(Number(d10 + d18 + d14) * getPercent());
      }
    }

    return;
  }, [
    fertilizationValues,
    getY1CurrentLoss,
    boardStatusValues,
    getY1CurrentProfit,
  ]);

  const getY2NpkProfit = useMemo(() => {
    if (fertilizationValues && getY2CurrentLoss && boardStatusValues) {
      const lossesPriceN = Number(unMaskInput(fertilizationValues?.nprice));
      const lossesKgN = Number(unMaskInput(fertilizationValues?.nvalue));
      const d9 = () => {
        if (Number(getY2CurrentLoss) > 6.5) {
          return 0;
        } else if (Number(getY2CurrentProfit) < 6.501) {
          const left = 1 - (30 * Number(getY2CurrentLoss) - 95) / 100;
          const right = 1 - (30 * Number(getY2CurrentProfit) - 95) / 100;

          return left - right;
        } else {
          const left = 1 - (30 * Number(getY2CurrentLoss) - 95) / 100;
          const right = 1 - (30 * 6.5 - 95) / 100;

          return left - right;
        }
      };
      const d10 = lossesKgN * lossesPriceN * Number(d9().toFixed(3));

      const lossesPriceK = Number(unMaskInput(fertilizationValues?.kprice));
      const lossesKgK = Number(unMaskInput(fertilizationValues?.kvalue));
      const d17 = () => {
        if (Number(getY2CurrentLoss) > 6) {
          return 0;
        } else if (Number(getY2CurrentProfit) < 6.001) {
          const left = 1 - (40 * Number(getY2CurrentLoss) - 140) / 100;
          const right = 1 - (40 * Number(getY2CurrentProfit) - 140) / 100;

          return left - right;
        } else {
          const left = 1 - (40 * Number(getY2CurrentLoss) - 140) / 100;
          const right = 1 - (40 * 6 - 140) / 100;

          return left - right;
        }
      };
      const d18 = lossesKgK * lossesPriceK * Number(d17().toFixed(3));

      const lossesPriceP = Number(unMaskInput(fertilizationValues?.pprice));
      const lossesKgP = Number(unMaskInput(fertilizationValues?.pvalue));
      const d13 = () => {
        if (Number(getY2CurrentLoss) > 6.5) {
          return 0;
        } else if (Number(getY2CurrentProfit) < 6.501) {
          const left = 1 - (35 * Number(getY2CurrentLoss) - 127.5) / 100;
          const right = 1 - (35 * Number(getY2CurrentProfit) - 127.5) / 100;

          return left - right;
        } else {
          const left = 1 - (35 * Number(getY2CurrentLoss) - 127.5) / 100;
          const right = 1 - (35 * 6.5 - 127.5) / 100;

          return left - right;
        }
      };
      const d14 = lossesKgP * lossesPriceP * Number(d13().toFixed(3));

      if (
        Number(boardStatusValues?.currentPh) <
        Number(boardStatusValues?.targetPh)
      ) {
        return Math.round(d10 + d18 + d14);
      } else {
        const getPercent = () => {
          if (Number(boardSoilValues?.split) === 1) {
            return 1;
          }

          if (Number(boardSoilValues?.split) === 2) {
            return 1 - 0.1;
          }

          if (Number(boardSoilValues?.split) === 3) {
            return 1 - 0.2;
          }

          if (Number(boardSoilValues?.split) === 4) {
            return 1 - 0.3;
          }

          if (Number(boardSoilValues?.split) === 5) {
            return 1 - 0.35;
          }

          return 0;
        };

        return Math.round(Number(d10 + d18 + d14) * getPercent());
      }
    }

    return;
  }, [fertilizationValues, getY2CurrentLoss, getY2CurrentProfit, boardStatusValues]);

  const getY3NpkProfit = useMemo(() => {
    if (fertilizationValues && getY3CurrentLoss && boardStatusValues) {
      const lossesPriceN = Number(unMaskInput(fertilizationValues?.nprice));
      const lossesKgN = Number(unMaskInput(fertilizationValues?.nvalue));
      const d9 = () => {
        if (Number(getY3CurrentLoss) > 6.5) {
          return 0;
        } else if (Number(getY3CurrentProfit) < 6.501) {
          const left = 1 - (30 * Number(getY3CurrentLoss) - 95) / 100;
          const right = 1 - (30 * Number(getY3CurrentProfit) - 95) / 100;

          return left - right;
        } else {
          const left = 1 - (30 * Number(getY3CurrentLoss) - 95) / 100;
          const right = 1 - (30 * 6.5 - 95) / 100;

          return left - right;
        }
      };
      const d10 = lossesKgN * lossesPriceN * Number(d9().toFixed(3));

      const lossesPriceK = Number(unMaskInput(fertilizationValues?.kprice));
      const lossesKgK = Number(unMaskInput(fertilizationValues?.kvalue));
      const d17 = () => {
        if (Number(getY3CurrentLoss) > 6) {
          return 0;
        } else if (Number(getY3CurrentProfit) < 6.001) {
          const left = 1 - (40 * Number(getY3CurrentLoss) - 140) / 100;
          const right = 1 - (40 * Number(getY3CurrentProfit) - 140) / 100;

          return left - right;
        } else {
          const left = 1 - (40 * Number(getY3CurrentLoss) - 140) / 100;
          const right = 1 - (40 * 6 - 140) / 100;

          return left - right;
        }
      };
      const d18 = lossesKgK * lossesPriceK * Number(d17().toFixed(3));

      const lossesPriceP = Number(unMaskInput(fertilizationValues?.pprice));
      const lossesKgP = Number(unMaskInput(fertilizationValues?.pvalue));
      const d13 = () => {
        if (Number(getY3CurrentLoss) > 6.5) {
          return 0;
        } else if (Number(getY3CurrentProfit) < 6.501) {
          const left = 1 - (35 * Number(getY3CurrentLoss) - 127.5) / 100;
          const right = 1 - (35 * Number(getY3CurrentProfit) - 127.5) / 100;

          return left - right;
        } else {
          const left = 1 - (35 * Number(getY3CurrentLoss) - 127.5) / 100;
          const right = 1 - (35 * 6.5 - 127.5) / 100;

          return left - right;
        }
      };
      const d14 = lossesKgP * lossesPriceP * Number(d13().toFixed(3));

      if (
        Number(boardStatusValues?.currentPh) <
        Number(boardStatusValues?.targetPh)
      ) {
        return Math.round(d10 + d18 + d14);
      } else {
        const getPercent = () => {
          if (Number(boardSoilValues?.split) === 1) {
            return 1;
          }

          if (Number(boardSoilValues?.split) === 2) {
            return 1 - 0.1;
          }

          if (Number(boardSoilValues?.split) === 3) {
            return 1 - 0.2;
          }

          if (Number(boardSoilValues?.split) === 4) {
            return 1 - 0.3;
          }

          if (Number(boardSoilValues?.split) === 5) {
            return 1 - 0.35;
          }

          return 0;
        };

        return Math.round(Number(d10 + d18 + d14) * getPercent());
      }
    }

    return;
  }, [fertilizationValues, getY3CurrentLoss, boardStatusValues]);

  const getY4NpkProfit = useMemo(() => {
    if (fertilizationValues && getY4CurrentLoss && boardStatusValues) {
      const lossesPriceN = Number(unMaskInput(fertilizationValues?.nprice));
      const lossesKgN = Number(unMaskInput(fertilizationValues?.nvalue));
      const d9 = () => {
        if (Number(getY4CurrentLoss) > 6.5) {
          return 0;
        } else if (Number(getY4CurrentProfit) < 6.501) {
          const left = 1 - (30 * Number(getY4CurrentLoss) - 95) / 100;
          const right = 1 - (30 * Number(getY4CurrentProfit) - 95) / 100;

          return left - right;
        } else {
          const left = 1 - (30 * Number(getY4CurrentLoss) - 95) / 100;
          const right = 1 - (30 * 6.5 - 95) / 100;

          return left - right;
        }
      };
      const d10 = lossesKgN * lossesPriceN * Number(d9().toFixed(3));

      const lossesPriceK = Number(unMaskInput(fertilizationValues?.kprice));
      const lossesKgK = Number(unMaskInput(fertilizationValues?.kvalue));
      const d17 = () => {
        if (Number(getY4CurrentLoss) > 6) {
          return 0;
        } else if (Number(getY4CurrentProfit) < 6.001) {
          const left = 1 - (40 * Number(getY4CurrentLoss) - 140) / 100;
          const right = 1 - (40 * Number(getY4CurrentProfit) - 140) / 100;

          return left - right;
        } else {
          const left = 1 - (40 * Number(getY4CurrentLoss) - 140) / 100;
          const right = 1 - (40 * 6 - 140) / 100;

          return left - right;
        }
      };
      const d18 = lossesKgK * lossesPriceK * Number(d17().toFixed(3));

      const lossesPriceP = Number(unMaskInput(fertilizationValues?.pprice));
      const lossesKgP = Number(unMaskInput(fertilizationValues?.pvalue));
      const d13 = () => {
        if (Number(getY4CurrentLoss) > 6.5) {
          return 0;
        } else if (Number(getY4CurrentProfit) < 6.501) {
          const left = 1 - (35 * Number(getY4CurrentLoss) - 127.5) / 100;
          const right = 1 - (35 * Number(getY4CurrentProfit) - 127.5) / 100;

          return left - right;
        } else {
          const left = 1 - (35 * Number(getY4CurrentLoss) - 127.5) / 100;
          const right = 1 - (35 * 6.5 - 127.5) / 100;

          return left - right;
        }
      };
      const d14 = lossesKgP * lossesPriceP * Number(d13().toFixed(3));

      if (
        Number(boardStatusValues?.currentPh) <
        Number(boardStatusValues?.targetPh)
      ) {
        return Math.round(d10 + d18 + d14);
      } else {
        const getPercent = () => {
          if (Number(boardSoilValues?.split) === 1) {
            return 1;
          }

          if (Number(boardSoilValues?.split) === 2) {
            return 1 - 0.1;
          }

          if (Number(boardSoilValues?.split) === 3) {
            return 1 - 0.2;
          }

          if (Number(boardSoilValues?.split) === 4) {
            return 1 - 0.3;
          }

          if (Number(boardSoilValues?.split) === 5) {
            return 1 - 0.35;
          }

          return 0;
        };

        return Math.round(Number(d10 + d18 + d14) * getPercent());
      }
    }

    return;
  }, [fertilizationValues, getY4CurrentLoss, boardStatusValues]);

  const getY5NpkProfit = useMemo(() => {
    if (fertilizationValues && getY5CurrentLoss && boardStatusValues) {
      const lossesPriceN = Number(unMaskInput(fertilizationValues?.nprice));
      const lossesKgN = Number(unMaskInput(fertilizationValues?.nvalue));
      const d9 = () => {
        if (Number(getY5CurrentLoss) > 6.5) {
          return 0;
        } else if (Number(getY5CurrentProfit) < 6.501) {
          const left = 1 - (30 * Number(getY5CurrentLoss) - 95) / 100;
          const right = 1 - (30 * Number(getY5CurrentProfit) - 95) / 100;

          return left - right;
        } else {
          const left = 1 - (30 * Number(getY5CurrentLoss) - 95) / 100;
          const right = 1 - (30 * 6.5 - 95) / 100;

          return left - right;
        }
      };
      const d10 = lossesKgN * lossesPriceN * Number(d9().toFixed(3));

      const lossesPriceK = Number(unMaskInput(fertilizationValues?.kprice));
      const lossesKgK = Number(unMaskInput(fertilizationValues?.kvalue));
      const d17 = () => {
        if (Number(getY5CurrentLoss) > 6) {
          return 0;
        } else if (Number(getY5CurrentProfit) < 6.001) {
          const left = 1 - (40 * Number(getY5CurrentLoss) - 140) / 100;
          const right = 1 - (40 * Number(getY5CurrentProfit) - 140) / 100;

          return left - right;
        } else {
          const left = 1 - (40 * Number(getY5CurrentLoss) - 140) / 100;
          const right = 1 - (40 * 6 - 140) / 100;

          return left - right;
        }
      };
      const d18 = lossesKgK * lossesPriceK * Number(d17().toFixed(3));

      const lossesPriceP = Number(unMaskInput(fertilizationValues?.pprice));
      const lossesKgP = Number(unMaskInput(fertilizationValues?.pvalue));
      const d13 = () => {
        if (Number(getY5CurrentLoss) > 6.5) {
          return 0;
        } else if (Number(getY5CurrentProfit) < 6.501) {
          const left = 1 - (35 * Number(getY5CurrentLoss) - 127.5) / 100;
          const right = 1 - (35 * Number(getY5CurrentProfit) - 127.5) / 100;

          return left - right;
        } else {
          const left = 1 - (35 * Number(getY5CurrentLoss) - 127.5) / 100;
          const right = 1 - (35 * 6.5 - 127.5) / 100;

          return left - right;
        }
      };
      const d14 = lossesKgP * lossesPriceP * Number(d13().toFixed(3));

      if (
        Number(boardStatusValues?.currentPh) <
        Number(boardStatusValues?.targetPh)
      ) {
        return Math.round(d10 + d18 + d14);
      } else {
        const getPercent = () => {
          if (Number(boardSoilValues?.split) === 1) {
            return 1;
          }

          if (Number(boardSoilValues?.split) === 2) {
            return 1 - 0.1;
          }

          if (Number(boardSoilValues?.split) === 3) {
            return 1 - 0.2;
          }

          if (Number(boardSoilValues?.split) === 4) {
            return 1 - 0.3;
          }

          if (Number(boardSoilValues?.split) === 5) {
            return 1 - 0.35;
          }

          return 0;
        };

        return Math.round(Number(d10 + d18 + d14) * getPercent());
      }
    }

    return;
  }, [fertilizationValues, getY5CurrentLoss, boardStatusValues]);

  const totalNpKProfit = useMemo(() => {
    if (
      getY1NpkProfit &&
      getY2NpkProfit &&
      getY3NpkProfit &&
      getY4NpkProfit &&
      getY5NpkProfit
    ) {
      return (
        getY1NpkProfit +
        getY2NpkProfit +
        getY3NpkProfit +
        getY4NpkProfit +
        getY5NpkProfit
      );
    }

    return;
  }, [
    getY1NpkProfit,
    getY2NpkProfit,
    getY3NpkProfit,
    getY4NpkProfit,
    getY5NpkProfit,
  ]);

  return {
    getY1NpkProfit,
    getY2NpkProfit,
    getY3NpkProfit,
    getY4NpkProfit,
    getY5NpkProfit,
    totalNpKProfit,
  };
};

export default useNpkProfit;
